import React from 'react';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import type { SearchFilters as SearchFiltersType, SearchEntityType, TripCategory, SearchSortBy } from '../types';

interface SearchFiltersProps {
  filters: SearchFiltersType;
  onChange: (filters: SearchFiltersType) => void;
  onReset: () => void;
}

const ENTITY_TYPES: { value: SearchEntityType; label: string }[] = [
  { value: 'user', label: 'Users' },
  { value: 'trip', label: 'Trips' },
  { value: 'pin', label: 'Pins' },
  { value: 'photo', label: 'Photos' },
];

const CATEGORIES: { value: TripCategory; label: string }[] = [
  { value: 'leisure', label: 'Leisure' },
  { value: 'business', label: 'Business' },
  { value: 'adventure', label: 'Adventure' },
  { value: 'cultural', label: 'Cultural' },
  { value: 'gastronomic', label: 'Gastronomic' },
  { value: 'other', label: 'Other' },
];

const SORT_OPTIONS: { value: SearchSortBy; label: string }[] = [
  { value: 'relevance', label: 'Relevance' },
  { value: 'date', label: 'Date' },
  { value: 'distance', label: 'Distance' },
];

/**
 * Filter sidebar/panel for search results.
 * Supports entity type, category, country, date range, and sort.
 */
export function SearchFiltersPanel({ filters, onChange, onReset }: SearchFiltersProps) {
  const handleChange = (field: keyof SearchFiltersType, value: string | undefined) => {
    onChange({ ...filters, [field]: value || undefined });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2 }}>
      <Typography variant="subtitle2" fontWeight={600}>
        Filters
      </Typography>

      <FormControl size="small" fullWidth>
        <InputLabel id="filter-type-label">Entity Type</InputLabel>
        <Select
          labelId="filter-type-label"
          value={filters.type || ''}
          label="Entity Type"
          onChange={(e) => handleChange('type', e.target.value as SearchEntityType)}
        >
          <MenuItem value="">All</MenuItem>
          {ENTITY_TYPES.map((t) => (
            <MenuItem key={t.value} value={t.value}>
              {t.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" fullWidth>
        <InputLabel id="filter-category-label">Category</InputLabel>
        <Select
          labelId="filter-category-label"
          value={filters.category || ''}
          label="Category"
          onChange={(e) => handleChange('category', e.target.value as TripCategory)}
        >
          <MenuItem value="">All</MenuItem>
          {CATEGORIES.map((c) => (
            <MenuItem key={c.value} value={c.value}>
              {c.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        label="Country Code"
        size="small"
        fullWidth
        value={filters.country || ''}
        onChange={(e) => handleChange('country', e.target.value)}
        placeholder="e.g. BR, US"
        inputProps={{ maxLength: 2 }}
      />

      <Divider />

      <Typography variant="caption" color="text.secondary">
        Date Range
      </Typography>

      <TextField
        label="From"
        type="date"
        size="small"
        fullWidth
        value={filters.dateFrom || ''}
        onChange={(e) => handleChange('dateFrom', e.target.value)}
        InputLabelProps={{ shrink: true }}
      />

      <TextField
        label="To"
        type="date"
        size="small"
        fullWidth
        value={filters.dateTo || ''}
        onChange={(e) => handleChange('dateTo', e.target.value)}
        InputLabelProps={{ shrink: true }}
      />

      <Divider />

      <FormControl size="small" fullWidth>
        <InputLabel id="filter-sort-label">Sort By</InputLabel>
        <Select
          labelId="filter-sort-label"
          value={filters.sortBy || 'relevance'}
          label="Sort By"
          onChange={(e) => handleChange('sortBy', e.target.value as SearchSortBy)}
        >
          {SORT_OPTIONS.map((s) => (
            <MenuItem key={s.value} value={s.value}>
              {s.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Button variant="outlined" size="small" onClick={onReset} sx={{ mt: 1 }}>
        Clear Filters
      </Button>
    </Box>
  );
}
